import React from "react";
import { Grid2 as Grid, CircularProgress, Typography, Box } from "@mui/material";
import { FieldValues, FormProvider } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import {
  EntityController,
  EntityHandlerContext,
  EntityHandlerReturn,
  useEntityHandler,
} from "../../controllers/entityController";
import FormActions from "./common/base/FormActions";
import FormHeader from "./common/base/FormHeader";
import FormFooter from "./common/base/FormFooter";
import AttributeToggleControl from "./attributeMetadata/AttributeToggleControl";
import SectionsWrapper from "../common/SectionsWrapper";
import { MainPaper } from "../common/Papers";
import GeneralAlert from "../common/GeneralAlert";
import { useFieldHashNavigation } from "../../hooks/useFieldHashNavigation";
import { useSnackbar } from "notistack";

interface FormPageProps<T extends FieldValues> {
  controller: EntityController<T>;
  children?: React.ReactNode;
  sections?: (data: T) => React.ReactElement[];
}

const ensureMetadataOpen = (): boolean => {
  const btn = document.querySelector(
    'button[aria-label*="Metadata"]'
  ) as HTMLButtonElement | null;

  if (!btn) return false;

  const label = (btn.getAttribute("aria-label") || "").toLowerCase();

  if (label.includes("display")) {
    btn.click();
    return true;
  }

  return false;
};

const FormPage = <T extends FieldValues>({
  controller,
  children,
  sections,
}: FormPageProps<T>) => {
  const entityHandler: EntityHandlerReturn<T> = useEntityHandler<T>(controller);
  const {
    methods,
    title,
    loading,
    onSubmit,
    fetchError,
    isCreateMode,
    hasMetadata,
  } = entityHandler;

  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  // Handle automatic scrolling to field when URL contains hash
  useFieldHashNavigation(loading);

  const renderSections = React.useMemo(() => {
    return sections !== undefined && !isCreateMode ? (
      <SectionsWrapper sections={sections} />
    ) : null;
  }, [sections, isCreateMode]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (fetchError) {
    return (
      <GeneralAlert
        errorMessage={fetchError}
        action={() => navigate(0)}
        actionLabel="Retry"
      />
    );
  }

  const handleSubmitWithWarning: React.FormEventHandler<
    HTMLFormElement
  > = async (e) => {
    e.preventDefault();

    const isValid = await methods.trigger();

    if (!isValid) {
      requestAnimationFrame(() => {
        const invalidSelector =
          'input[aria-invalid="true"], textarea[aria-invalid="true"], [aria-invalid="true"][role="combobox"]';

        const el = document.querySelector(
          invalidSelector
        ) as HTMLElement | null;
        if (!el) return;

        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.focus?.();

        setTimeout(() => {
          ensureMetadataOpen();
        }, 750);
      });

      enqueueSnackbar(
        "Some mandatory fields are empty. Please fill them before updating.",
        { variant: "warning" }
      );
      return;
    }

    await onSubmit();
  };

  return (
    <EntityHandlerContext.Provider value={entityHandler}>
      <FormProvider {...methods}>
        <MainPaper>
          <Box
            sx={{
              mb: 4,
              position: "relative",
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
            }}
          >
            {hasMetadata ? (
              <Box
                sx={{
                  position: "absolute",
                  right: 0,
                }}
              >
                <AttributeToggleControl />
              </Box>
            ) : null}
            {title && (() => {
              const [publishedIdTitle, workNameTitle] = title.split('\n');
              return (
                <>
                  <Typography variant="h2">{publishedIdTitle}</Typography>
                  {workNameTitle && (
                    <Typography variant="h2" sx={{ mt: 0.5, fontSize: "calc(1.1em)" }}>
                      {workNameTitle}
                    </Typography>
                  )}
                </>
              );
            })()}
          </Box>

          <form onSubmit={handleSubmitWithWarning} noValidate>
            <Grid container spacing={2}>
              <FormHeader />
              {children}
              <FormFooter />
              <FormActions />
            </Grid>
          </form>
        </MainPaper>

        {renderSections}
      </FormProvider>
    </EntityHandlerContext.Provider>
  );
};
//
export default FormPage;
